import styled, { css, keyframes } from "styled-components";

const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease,
    box-shadow 180ms ease, color 180ms ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const firePulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.78;
  }

  50% {
    transform: scale(1.14);
    opacity: 1;
  }

  100% {
    transform: scale(1);
    opacity: 0.78;
  }
`;

export const Page = styled.main`
  --bg: #0f1012;
  --bg-elevated: #17181c;
  --bg-card: #1f2024;
  --bg-card-strong: #26272d;
  --border: rgba(255, 255, 255, 0.08);
  --border-strong: rgba(255, 255, 255, 0.14);
  --text: #f5f6f7;
  --text-muted: rgba(245, 246, 247, 0.76);
  --text-soft: rgba(245, 246, 247, 0.54);
  --accent-pink: #f23d7f;
  --accent-orange: #ff8a00;
  --accent-red: #ff5c42;
  --accent-gold: #f8d29a;

  position: relative;
  overflow: hidden;
  min-height: 100vh;
  background:
    radial-gradient(circle at 12% 12%, rgba(242, 61, 127, 0.18), transparent 24%),
    radial-gradient(circle at 84% 10%, rgba(255, 138, 0, 0.2), transparent 24%),
    linear-gradient(180deg, #151519 0%, #0f1012 100%);
  color: var(--text);
`;

export const Glow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 24% 28%, rgba(255, 92, 66, 0.14), transparent 18%),
    radial-gradient(circle at 78% 68%, rgba(242, 61, 127, 0.12), transparent 22%),
    radial-gradient(circle at 66% 18%, rgba(255, 138, 0, 0.08), transparent 16%);
`;

export const Shell = styled.div`
  position: relative;
  z-index: 1;
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 24px 0 56px;

  @media (max-width: 768px) {
    width: min(100% - 28px, 1180px);
    padding-bottom: 40px;
  }
`;

export const TopBar = styled.header`
  position: sticky;
  top: 16px;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 22px;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: rgba(16, 16, 19, 0.74);
  backdrop-filter: blur(18px);

  @media (max-width: 980px) {
    flex-wrap: wrap;
  }

  @media (max-width: 640px) {
    padding: 16px 18px;
    border-radius: 20px;
  }
`;

export const BrandLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  color: var(--text);
`;

export const BrandMark = styled.img`
  width: 132px;
  height: auto;
  object-fit: contain;

  @media (max-width: 640px) {
    width: 110px;
  }
`;

export const BrandCopy = styled.span`
  max-width: 250px;
  color: var(--text-soft);
  font-size: 0.92rem;
  line-height: 1.45;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLinks = styled.nav`
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 22px;

  @media (max-width: 960px) {
    order: 3;
    width: 100%;
    justify-content: flex-start;
    gap: 14px;
  }
`;

export const NavLink = styled.a`
  color: var(--text-muted);
  font-size: 0.94rem;
  font-weight: 500;
  transition: color 180ms ease;

  &:hover {
    color: var(--text);
  }
`;

export const TopAction = styled.a`
  ${buttonStyles};
  background: linear-gradient(135deg, var(--accent-orange), var(--accent-pink));
  color: #fff;
  box-shadow: 0 18px 36px rgba(242, 61, 127, 0.16);

  @media (max-width: 960px) {
    margin-left: auto;
  }
`;

export const IntroBanner = styled.div`
  margin-top: 20px;
  border-radius: 30px;
  overflow: hidden;
  border: 1px solid var(--border);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.02)),
    rgba(16, 16, 19, 0.92);
  box-shadow: 0 22px 52px rgba(0, 0, 0, 0.22);

  @media (max-width: 640px) {
    margin-top: 16px;
    border-radius: 22px;
  }
`;

export const IntroBannerCaption = styled.p`
  max-width: 860px;
  margin: 24px auto 12px;
  color: var(--text-muted);
  font-size: 1.12rem;
  line-height: 1.7;
  text-align: center;

  @media (max-width: 640px) {
    margin: 18px auto 10px;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

export const IntroBannerImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

export const HeroSection = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 0.96fr) minmax(340px, 1.04fr);
  gap: 34px;
  padding: 72px 0 44px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding-top: 48px;
  }

  @media (max-width: 640px) {
    gap: 24px;
    padding: 40px 0 30px;
  }
`;

export const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const HeroEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--accent-gold);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

export const HeroTitle = styled.h1`
  max-width: 720px;
  font-family: "Space Grotesk", sans-serif;
  font-size: 29px;
  line-height: 0.96;
  letter-spacing: -0.055em;
`;

export const HeroDescription = styled.p`
  max-width: 670px;
  color: var(--text-muted);
  font-size: 1.04rem;
  line-height: 1.78;

  @media (max-width: 640px) {
    font-size: 0.98rem;
    line-height: 1.68;
  }
`;

export const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const PrimaryButton = styled.a`
  ${buttonStyles};
  background: linear-gradient(135deg, var(--accent-orange), var(--accent-pink));
  color: #fff;
  box-shadow: 0 18px 42px rgba(242, 61, 127, 0.16);
`;

export const SecondaryButton = styled.a`
  ${buttonStyles};
  border-color: var(--border-strong);
  background: rgba(255, 255, 255, 0.03);
  color: var(--text);
`;

export const ButtonStack = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

export const ButtonIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.16rem;
  line-height: 1;
`;

export const ButtonCopy = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  text-align: left;
`;

export const ButtonTitle = styled.span`
  font-size: 0.94rem;
  font-weight: 700;
  line-height: 1.1;
`;

export const ButtonHint = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1.1;
  opacity: 0.74;
  text-transform: uppercase;
`;

export const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.article`
  padding: 18px 18px 20px;
  border-radius: 22px;
  border: 1px solid var(--border);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.02)),
    rgba(20, 20, 24, 0.9);
`;

export const StatTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

export const StatValue = styled.strong`
  display: block;
  margin-bottom: 10px;
  font-family: "Space Grotesk", sans-serif;
  font-size: 1.12rem;
  letter-spacing: -0.04em;
`;

export const StatValueInline = styled.strong`
  display: block;
  font-family: "Space Grotesk", sans-serif;
  font-size: 1.12rem;
  letter-spacing: -0.04em;
`;

export const StatLabel = styled.span`
  display: block;
  color: var(--text-soft);
  font-size: 0.92rem;
  line-height: 1.58;
`;

export const StatDetail = styled.span`
  display: block;
  margin-top: 8px;
  color: rgba(245, 246, 247, 0.6);
  font-size: 0.84rem;
  line-height: 1.55;
`;

export const HeroMedia = styled.div`
  display: flex;
  align-items: stretch;
`;

export const DemoSurface = styled.div`
  position: relative;
  width: 100%;
  padding: 18px;
  border-radius: 34px;
  border: 1px solid var(--border);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.02)),
    rgba(16, 16, 19, 0.92);
  box-shadow: 0 26px 84px rgba(0, 0, 0, 0.3);

  &::before {
    content: "";
    position: absolute;
    inset: 12px;
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    pointer-events: none;
  }

  @media (max-width: 640px) {
    padding: 14px;
    border-radius: 28px;
  }
`;

export const DemoGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 0.9fr);
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const shellSurface = css`
  padding: 18px;
  border-radius: 24px;
  border: 1px solid var(--border);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.02)),
    rgba(23, 24, 29, 0.96);
`;

export const FeedShell = styled.article`
  ${shellSurface};
`;

export const FeedPreviewImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
`;

export const MapShell = styled.article`
  ${shellSurface};
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ShellHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
`;

export const ShellEyebrow = styled.span`
  color: var(--accent-gold);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const ShellTitle = styled.strong`
  font-family: "Space Grotesk", sans-serif;
  font-size: 1.18rem;
  line-height: 1.15;
  letter-spacing: -0.03em;
`;

export const ShellText = styled.span`
  color: var(--text-soft);
  font-size: 0.9rem;
  line-height: 1.55;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const DemoChip = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(245, 246, 247, 0.92);
  font-size: 0.78rem;
  font-weight: 600;
`;

export const FeedList = styled.div`
  display: grid;
  gap: 12px;
`;

export const FeedCard = styled.article`
  padding: 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
`;

export const FeedBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  min-height: 28px;
  padding: 0 10px;
  align-items: center;
  border-radius: 999px;
  background: rgba(242, 61, 127, 0.14);
  color: #ffc0d8;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const FeedTitle = styled.h3`
  margin: 12px 0 8px;
  font-size: 1.04rem;
  line-height: 1.28;
`;

export const FeedMeta = styled.span`
  display: block;
  color: var(--text-soft);
  font-size: 0.88rem;
  line-height: 1.55;
`;

export const FeedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

export const FeedTag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(245, 246, 247, 0.82);
  font-size: 0.74rem;
  font-weight: 600;
`;

export const MapCanvas = styled.div`
  position: relative;
  min-height: 260px;
  border-radius: 22px;
  overflow: hidden;
  background:
    radial-gradient(circle at 32% 30%, rgba(255, 92, 66, 0.18), transparent 0 16%),
    radial-gradient(circle at 68% 24%, rgba(242, 61, 127, 0.2), transparent 0 14%),
    radial-gradient(circle at 64% 62%, rgba(255, 138, 0, 0.18), transparent 0 14%),
    linear-gradient(180deg, rgba(30, 31, 36, 0.96), rgba(18, 19, 22, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.06);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 38px 38px;
    opacity: 0.24;
  }
`;

export const MapPreviewImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

export const MapRangeBadge = styled.span`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(16, 16, 19, 0.74);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text);
  backdrop-filter: blur(10px);
`;

const heatStyles = {
  hot: css`
    &::before {
      background: rgba(255, 92, 66, 0.26);
      box-shadow: 0 0 0 10px rgba(255, 92, 66, 0.12), 0 0 28px rgba(255, 92, 66, 0.28);
    }
  `,
  warm: css`
    &::before {
      background: rgba(242, 61, 127, 0.24);
      box-shadow: 0 0 0 10px rgba(242, 61, 127, 0.12), 0 0 24px rgba(242, 61, 127, 0.24);
    }
  `,
  rising: css`
    &::before {
      background: rgba(255, 138, 0, 0.24);
      box-shadow: 0 0 0 10px rgba(255, 138, 0, 0.12), 0 0 24px rgba(255, 138, 0, 0.24);
    }
  `,
};

export const MapPinButton = styled.div<{ $heat: "hot" | "warm" | "rising" }>`
  position: absolute;
  z-index: 2;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(14, 14, 18, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(12px);

  &::before {
    content: "";
    position: absolute;
    inset: -2px;
    border-radius: 999px;
    z-index: -1;
  }

  ${(props) => heatStyles[props.$heat]}
`;

export const MapPinDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--accent-orange), var(--accent-pink));
`;

export const MapPinLabel = styled.span`
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--text);
`;

export const MapLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: var(--text-soft);
  font-size: 0.75rem;
  font-weight: 600;
`;

export const MiniCards = styled.div`
  display: grid;
  gap: 10px;
`;

export const MiniFeatureCard = styled.article`
  padding: 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
`;

export const MiniFeatureBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--accent-gold);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const MiniFeatureTitle = styled.h3`
  margin: 12px 0 6px;
  font-size: 0.98rem;
  line-height: 1.32;
`;

export const MiniFeatureText = styled.p`
  color: var(--text-soft);
  font-size: 0.85rem;
  line-height: 1.56;
`;

export const ChatShowcase = styled.div`
  position: relative;
  margin-top: 16px;
  padding-top: 92px;
  border-radius: 20px;
`;

export const ChatEventCard = styled.div`
  position: relative;
  z-index: 2;
  padding: 16px;
  border-radius: 18px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.03)),
    rgba(28, 29, 34, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 18px 32px rgba(0, 0, 0, 0.22);
`;

export const ChatEventTitle = styled.h4`
  margin: 12px 0 6px;
  font-size: 1rem;
  line-height: 1.28;
`;

export const ChatEventMeta = styled.span`
  display: block;
  color: var(--text-soft);
  font-size: 0.8rem;
  line-height: 1.5;
`;

export const ChatBubbles = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 12px;
`;

export const ChatBubble = styled.div`
  position: absolute;
  z-index: 3;
  max-width: 190px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(16, 16, 20, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(245, 246, 247, 0.92);
  font-size: 0.74rem;
  line-height: 1.5;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.2);

  @media (max-width: 640px) {
    max-width: 160px;
  }
`;

export const ChatBubbleAuthor = styled.strong`
  display: block;
  margin-bottom: 4px;
  color: #ffc1d6;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const Section = styled.section`
  padding: 34px 0;
  scroll-margin-top: 112px;

  @media (max-width: 640px) {
    padding: 26px 0;
  }
`;

export const SectionHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 26px;
  max-width: 760px;
`;

export const SectionEyebrow = styled.span`
  color: var(--accent-gold);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const SectionTitle = styled.h2`
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(2rem, 4vw, 3.1rem);
  line-height: 1.02;
  letter-spacing: -0.05em;
`;

export const SectionDescription = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.78;
`;

export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureCard = styled.article`
  position: relative;
  padding: 26px;
  border-radius: 28px;
  border: 1px solid var(--border);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.02)),
    rgba(19, 20, 24, 0.94);
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: auto 0 0 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-orange), var(--accent-pink));
    opacity: 0.94;
  }
`;

export const FeatureIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  margin-bottom: 18px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  color: #ff9d6d;
  font-size: 1.18rem;
`;

export const FeatureEyebrow = styled.span`
  display: inline-flex;
  margin-bottom: 12px;
  color: var(--accent-gold);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

export const FeatureTitle = styled.h3`
  font-size: 1.24rem;
  line-height: 1.3;
`;

export const HeroStatBadge = styled.span<{ $variant?: "fire" | "message" | "radar" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 999px;
  background: ${({ $variant }) =>
    $variant === "message"
      ? "rgba(242, 61, 127, 0.16)"
      : $variant === "radar"
        ? "rgba(248, 210, 154, 0.14)"
        : "rgba(255, 59, 48, 0.16)"};
  border: 1px solid
    ${({ $variant }) =>
      $variant === "message"
        ? "rgba(242, 61, 127, 0.32)"
        : $variant === "radar"
          ? "rgba(248, 210, 154, 0.28)"
          : "rgba(255, 59, 48, 0.3)"};
  color: ${({ $variant }) =>
    $variant === "message"
      ? "#f23d7f"
      : $variant === "radar"
        ? "#f8d29a"
        : "#ff453a"};
  box-shadow: ${({ $variant }) =>
    $variant === "message"
      ? "0 0 0 8px rgba(242, 61, 127, 0.08)"
      : $variant === "radar"
        ? "0 0 0 8px rgba(248, 210, 154, 0.06)"
        : "0 0 0 8px rgba(255, 92, 66, 0.08)"};
  animation: ${({ $variant }) =>
    $variant === "fire"
      ? css`
          ${firePulse} 1.8s ease-in-out infinite
        `
      : "none"};
`;

export const FeatureDescription = styled.p`
  color: var(--text-soft);
  font-size: 0.98rem;
  line-height: 1.68;
`;

export const FeatureImpact = styled.p`
  margin-top: 16px;
  color: var(--text);
  font-size: 0.92rem;
  line-height: 1.58;
`;

export const LoopGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

export const LoopCard = styled.article`
  padding: 26px;
  border-radius: 24px;
  border: 1px solid var(--border);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.02)),
    rgba(22, 22, 26, 0.94);
`;

export const LoopStep = styled.span`
  display: inline-flex;
  margin-bottom: 18px;
  color: rgba(255, 255, 255, 0.42);
  font-family: "Space Grotesk", sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
`;

export const LoopTitle = styled.h3`
  margin-bottom: 12px;
  font-size: 1.16rem;
  line-height: 1.32;
`;

export const LoopText = styled.p`
  color: var(--text-soft);
  font-size: 0.96rem;
  line-height: 1.65;
`;

export const PillarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

export const PillarCard = styled.article`
  padding: 26px;
  border-radius: 24px;
  border: 1px solid var(--border);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.02)),
    rgba(18, 19, 22, 0.95);
`;

export const PillarTitle = styled.h3`
  margin-bottom: 12px;
  font-size: 1.14rem;
  line-height: 1.32;
`;

export const PillarText = styled.p`
  color: var(--text-soft);
  font-size: 0.96rem;
  line-height: 1.68;
`;

export const ClosingCard = styled.section`
  margin-top: 26px;
  padding: 34px;
  border-radius: 34px;
  border: 1px solid var(--border);
  background:
    radial-gradient(circle at top right, rgba(255, 138, 0, 0.16), transparent 32%),
    radial-gradient(circle at bottom left, rgba(242, 61, 127, 0.14), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02)),
    rgba(20, 20, 24, 0.94);

  @media (max-width: 640px) {
    padding: 24px;
    border-radius: 28px;
  }
`;

export const ClosingTitle = styled.h2`
  max-width: 820px;
  margin-bottom: 14px;
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(1.85rem, 3vw, 2.8rem);
  line-height: 1.04;
  letter-spacing: -0.05em;
`;

export const ClosingText = styled.p`
  max-width: 760px;
  margin-bottom: 22px;
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.78;
`;

export const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  padding: 34px 0 8px;

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const FooterNote = styled.p`
  max-width: 520px;
  color: var(--text-soft);
  font-size: 0.92rem;
  line-height: 1.6;
`;

export const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
`;

export const FooterAnchor = styled.a`
  color: var(--text-muted);
  font-size: 0.92rem;
  transition: color 180ms ease;

  &:hover {
    color: var(--text);
  }
`;
